import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    Container,
    FormField,
    Header,
    Input,
    Select,
    SpaceBetween,
    Textarea,
} from '@cloudscape-design/components';
import { AdminDemoDefiniton, DemoStatus, } from '../../types';

export interface DemoFormProps {
    demo?: AdminDemoDefiniton;
    onSubmit: (demo: AdminDemoDefiniton) => void;
    type?: 'add' | 'edit';
}

export default function DemoForm(props: DemoFormProps) {
    const { demo, onSubmit, type = 'add' } = props;
    const navigate = useNavigate();
    const [name, setName] = useState(demo?.name ?? '');
    const [description, setDescription] = useState(demo?.description ?? '');
    const [path, setPath] = useState(demo?.path ?? '');
    const [status, setStatus] = useState<DemoStatus | ''>(demo?.status ?? '');
    const [visible, setVisible] = useState(demo?.visible ?? true);
    const [infrastructureType, setInfrastructureType] = useState<'s3' | 'load-balanced' | ''>(demo?.infrastructure?.type ?? '');
    const [repositoryUrl, setRepositoryUrl] = useState(demo?.demoDetails?.repositoryUrl ?? '');
    const [repositoryName, setRepositoryName] = useState(demo?.demoDetails?.repositoryName ?? '');
    const [repositoryNotes, setRepositoryNotes] = useState(demo?.demoDetails?.repositoryNotes ?? '');
    const [features, setFeatures] = useState<string[]>(demo?.demoDetails?.features ?? []);
    const [useCases, setUseCases] = useState<string[]>(demo?.demoDetails?.useCases ?? []);
    const [imageUrl, setImageUrl] = useState(demo?.demoDetails?.imageUrl ?? '');
    const [videoUrl, setVideoUrl] = useState(demo?.demoDetails?.videoUrl ?? '');
    const [mainDescriptor, setMainDescriptor] = useState(demo?.demoDetails?.mainDescriptor ?? '');
    const [embed, setEmbed] = useState(demo?.demoDetails?.embed ?? false);
    const [rank, setRank] = useState(demo?.rank ?? 0);
    const [builderSpaceUrl, setBuilderSpaceUrl] = useState(demo?.demoDetails?.builderSpaceUrl ?? '');
    const [sfdcTagCategory, setSfdcTagCategory] = useState(demo?.demoDetails?.sfdcTagCategory ?? '');
    const [sfdcTagName, setSfdcTagName] = useState(demo?.demoDetails?.sfdcTagName ?? '');

    const [bucketName, setBucketName] = useState(demo?.infrastructure?.type === "s3" ? demo?.infrastructure?.bucketName : '');


    // Load balanced specific fields
    const [loadBalancerArn, setLoadBalancerArn] = useState(demo?.infrastructure?.type === "load-balanced" ? demo?.infrastructure?.loadBalancerArn : '');
    const [dnsName, setDnsName] = useState(demo?.infrastructure?.type === "load-balanced" ? demo?.infrastructure?.dnsName : '');
    const [vpcId, setVpcId] = useState(demo?.infrastructure?.type === "load-balanced" ? demo?.infrastructure?.vpcId : '');

    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (demo) {
            setName(demo.name || '');
            setDescription(demo.description || '');
            setPath(demo.path || '');
            setStatus(demo.status || '');
            setInfrastructureType(demo.infrastructure?.type || '');
            setRepositoryUrl(demo.demoDetails?.repositoryUrl || '');
            setRepositoryName(demo.demoDetails?.repositoryName || '');
            setRepositoryNotes(demo.demoDetails?.repositoryNotes || '');
            setFeatures(demo.demoDetails?.features || []);
            setUseCases(demo.demoDetails?.useCases || []);
            setImageUrl(demo.demoDetails?.imageUrl || '');
            setVideoUrl(demo.demoDetails?.videoUrl || '');
            setMainDescriptor(demo.demoDetails?.mainDescriptor || '');
            setEmbed(demo.demoDetails?.embed || false);
            setBuilderSpaceUrl(demo.demoDetails?.builderSpaceUrl || '');
            setSfdcTagCategory(demo.demoDetails?.sfdcTagCategory || '');
            setSfdcTagName(demo.demoDetails?.sfdcTagName || '');
            setBucketName(demo.infrastructure?.type === 's3' ? demo.infrastructure.bucketName || '' : '');
            setLoadBalancerArn(demo.infrastructure?.type === 'load-balanced' ? demo.infrastructure.loadBalancerArn || '' : '');
            setDnsName(demo.infrastructure?.type === 'load-balanced' ? demo.infrastructure.dnsName || '' : '');
            setVpcId(demo.infrastructure?.type === 'load-balanced' ? demo.infrastructure.vpcId || '' : '');
            setVisible(demo.visible ?? true);
            setRank(demo.rank ?? 0);
        }
    }, [demo]); // Add demo as dependency

    const handleDownloadConfig = () => {
        const infrastructure = infrastructureType === 's3'
            ? {
                type: 's3' as const,
                bucketName,
            }
            : infrastructureType === 'load-balanced'
                ? {
                    type: 'load-balanced' as const,
                    loadBalancerArn,
                    dnsName,
                    vpcId,
                }
                : undefined;

        const config: AdminDemoDefiniton = {
            name,
            description,
            path,
            status: (status.length > 0) ? status as DemoStatus : DemoStatus.production,
            visible,
            demoDetails: {
                repositoryUrl,
                repositoryName,
                repositoryNotes,
                features,
                useCases,
                imageUrl: imageUrl || undefined,
                videoUrl: videoUrl || undefined,
                mainDescriptor: mainDescriptor || undefined,
                builderSpaceUrl: builderSpaceUrl || undefined,
                sfdcTagCategory: sfdcTagCategory || undefined,
                sfdcTagName: sfdcTagName || undefined,
                embed
            },
            rank,
            ...(infrastructure && { infrastructure })
        };

        const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name.toLowerCase().replace(/\s+/g, '-')}-config.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleUploadConfig = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const config: AdminDemoDefiniton = JSON.parse(e.target?.result as string);
                setName(config.name);
                setDescription(config.description ?? '');
                setPath(config.path ?? '');
                setStatus(config.status ?? '');
                setRepositoryUrl(config.demoDetails?.repositoryUrl ?? '');
                setRepositoryName(config.demoDetails?.repositoryName ?? '');
                setRepositoryNotes(config.demoDetails?.repositoryNotes ?? '');
                setFeatures(config.demoDetails?.features ?? []);
                setUseCases(config.demoDetails?.useCases ?? []);
                setImageUrl(config.demoDetails?.imageUrl || '');
                setVideoUrl(config.demoDetails?.videoUrl || '');
                setMainDescriptor(config.demoDetails?.mainDescriptor || '');
                setBuilderSpaceUrl(config.demoDetails?.builderSpaceUrl || '');
                setSfdcTagCategory(config.demoDetails?.sfdcTagCategory || '');
                setSfdcTagName(config.demoDetails?.sfdcTagName || '');
                setEmbed(config.demoDetails?.embed ?? false);
                setVisible(config.visible ?? true);
                setRank(config.rank ?? 0);
                if (config.infrastructure) {
                    setInfrastructureType(config.infrastructure.type);
                    if (config.infrastructure.type === 's3') {
                        setBucketName(config.infrastructure.bucketName ?? '');
                    } else if (config.infrastructure.type === 'load-balanced') {
                        setLoadBalancerArn(config.infrastructure.loadBalancerArn ?? '');
                        setDnsName(config.infrastructure.dnsName);
                        setVpcId(config.infrastructure.vpcId);
                    }
                }
            } catch (error) {
                console.error('Error parsing config file:', error);
                alert('Invalid config file format');
            }
        };
        reader.readAsText(file);
    };

    const handleSubmit = async () => {
        if (!name || !description || !path || !status) {
            // Add error handling here
            return;
        }

        const infrastructure = infrastructureType === 's3'
            ? {
                type: 's3' as const,
                bucketName,
            }
            : infrastructureType === 'load-balanced'
                ? {
                    type: 'load-balanced' as const,
                    loadBalancerArn,
                    dnsName,
                    vpcId,
                }
                : undefined;

        const newDemo: AdminDemoDefiniton = {
            name,
            description,
            path,
            status,  // Status is now at top level
            visible,
            demoDetails: {
                repositoryUrl,
                repositoryName,
                repositoryNotes,
                features,
                useCases,
                imageUrl: imageUrl || undefined,
                videoUrl: videoUrl || undefined,
                mainDescriptor: mainDescriptor || undefined,
                builderSpaceUrl: builderSpaceUrl || undefined,
                sfdcTagCategory: sfdcTagCategory || undefined,
                sfdcTagName: sfdcTagName || undefined,
                embed
            },
            rank,
            ...(infrastructure && { infrastructure })
        };

        onSubmit(newDemo);

    };

    return (
        <SpaceBetween size="l">
            <Container header={<Header variant="h2">Add New Demo</Header>}>
                <SpaceBetween size="m">
                    <div style={{ marginBottom: '20px' }}>
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            accept="application/json"
                            onChange={handleUploadConfig}
                        />
                        <Button
                            onClick={() => fileInputRef.current?.click()}
                            variant='normal'
                        >
                            Upload Config
                        </Button>
                        <Button
                            onClick={handleDownloadConfig}
                        >
                            Download Config
                        </Button>
                    </div>
                    <FormField label="Name" constraintText="Required">
                        <Input
                            value={name}
                            onChange={e => setName(e.detail.value)}
                            placeholder="Enter demo name"
                            ariaRequired
                        />
                    </FormField>

                    <FormField label="Description" constraintText="Required">
                        <Textarea
                            value={description}
                            onChange={e => setDescription(e.detail.value)}
                            placeholder="Enter demo description"
                            ariaRequired
                        />
                    </FormField>

                    <FormField label="Path" constraintText="Required">
                        <Input
                            value={path}
                            onChange={e => setPath(e.detail.value)}
                            placeholder="Enter URL-friendly path"
                            ariaRequired
                        />
                    </FormField>

                    <FormField label="Visible">
                        <Select
                            selectedOption={visible ? { label: 'Yes', value: 'true' } : { label: 'No', value: 'false' }}
                            onChange={e => setVisible(e.detail.selectedOption.value === 'true')}
                            options={[
                                { label: 'Yes', value: 'true' },
                                { label: 'No', value: 'false' }
                            ]}
                        />
                    </FormField>

                    <FormField label="Status" constraintText="Required">
                        <Select
                            selectedOption={status ? { label: status, value: status } : null}
                            onChange={e => setStatus(e.detail.selectedOption?.value as DemoStatus)}
                            options={[
                                { label: 'Development', value: DemoStatus.development },
                                { label: 'Staging', value: DemoStatus.staging },
                                { label: 'Production', value: DemoStatus.production },
                                { label: 'Coming Soon', value: DemoStatus.comingSoon },
                            ]}
                            placeholder="Choose demo status"
                        />
                    </FormField>

                    <FormField label="Rank" constraintText="Optional">
                        <Input
                            value={rank.toString()}
                            onChange={e => setRank(parseInt(e.detail.value))}
                            placeholder="Enter demo rank"
                        />
                    </FormField>

                    <FormField label="Features">
                        <SpaceBetween size="xs">
                            {features.map((feature, index) => (
                                <SpaceBetween key={index} size="xs" direction="horizontal">
                                    <Input value={feature} onChange={e => {
                                        const newFeatures = [...features];
                                        newFeatures[index] = e.detail.value;
                                        setFeatures(newFeatures);
                                    }} />
                                    <Button iconName="delete-marker" onClick={() => {
                                        const newFeatures = features.filter((_, i) => i !== index);
                                        setFeatures(newFeatures);
                                    }} />
                                </SpaceBetween>
                            ))}
                            <Button onClick={() => setFeatures([...features, ''])}>+</Button>
                        </SpaceBetween>
                    </FormField>

                    <FormField label="Use Cases">
                        <SpaceBetween size="xs">
                            {useCases.map((useCase, index) => (
                                <SpaceBetween key={index} size="xs" direction="horizontal">
                                    <Input value={useCase} onChange={e => {
                                        const newUseCases = [...useCases];
                                        newUseCases[index] = e.detail.value;
                                        setUseCases(newUseCases);
                                    }} />
                                    <Button iconName="delete-marker" onClick={() => {
                                        const newUseCases = useCases.filter((_, i) => i !== index);
                                        setUseCases(newUseCases);
                                    }} />
                                </SpaceBetween>
                            ))}
                            <Button onClick={() => setUseCases([...useCases, ''])}>+</Button>
                        </SpaceBetween>
                    </FormField>

                    <FormField label="Repository Information">
                        <SpaceBetween size="m">
                            <Input
                                placeholder="Repository Name"
                                value={repositoryName}
                                onChange={e => setRepositoryName(e.detail.value)}
                            />
                            <Input
                                placeholder="Repository URL"
                                value={repositoryUrl}
                                onChange={e => setRepositoryUrl(e.detail.value)}
                            />
                            <Textarea
                                placeholder="Repository Notes"
                                value={repositoryNotes}
                                onChange={e => setRepositoryNotes(e.detail.value)}
                            />
                        </SpaceBetween>
                    </FormField>

                    <FormField label="SFDC Tag Information">
                        <SpaceBetween size="m">
                            <Select
                                selectedOption={sfdcTagCategory ? { label: sfdcTagCategory, value: sfdcTagCategory } : null}
                                onChange={e => setSfdcTagCategory(e.detail.selectedOption?.value || '')}
                                options={[
                                    { label: 'Solution', value: 'Solution' },
                                    { label: 'Use Case', value: 'Use Case' },
                                    { label: 'Guidance', value: 'Guidance' }
                                ]}
                                placeholder="Select SFDC Tag Category"
                            />
                            <Input
                                placeholder="SFDC Tag Name"
                                value={sfdcTagName}
                                onChange={e => setSfdcTagName(e.detail.value)}
                            />
                        </SpaceBetween>
                    </FormField>

                    <FormField label="Infrastructure Type">
                        <Select
                            selectedOption={infrastructureType ? { label: infrastructureType, value: infrastructureType } : null}
                            onChange={e => {
                                const newType = e.detail.selectedOption?.value as 's3' | 'load-balanced' | '';
                                setInfrastructureType(newType);
                                // Clear related fields when type is cleared or changed
                                if (!newType || newType !== 's3') {
                                    setBucketName('');
                                }
                                if (!newType || newType !== 'load-balanced') {
                                    setLoadBalancerArn('');
                                    setDnsName('');
                                    setVpcId('');
                                }
                            }}
                            options={[
                                { label: 'Reset to None', value: '' },
                                { label: 'S3', value: 's3' },
                                { label: 'Load Balanced', value: 'load-balanced' }
                            ]}
                            empty="Choose infrastructure type"
                            placeholder="Choose infrastructure type (optional)"
                        />
                    </FormField>

                    {infrastructureType === 's3' && (
                        <FormField label="S3 Bucket Name">
                            <Input value={bucketName || ''} onChange={e => setBucketName(e.detail.value)} />
                        </FormField>
                    )}

                    {infrastructureType === 'load-balanced' && (
                        <SpaceBetween size="m">
                            <FormField label="Load Balancer ARN">
                                <Input value={loadBalancerArn || ''} onChange={e => setLoadBalancerArn(e.detail.value)} />
                            </FormField>
                            <FormField label="DNS Name">
                                <Input value={dnsName || ''} onChange={e => setDnsName(e.detail.value)} />
                            </FormField>
                            <FormField label="VPC ID">
                                <Input value={vpcId || ''} onChange={e => setVpcId(e.detail.value)} />
                            </FormField>
                        </SpaceBetween>
                    )}

                    <FormField label="Main Descriptor (Optional)">
                        <Input
                            value={mainDescriptor}
                            onChange={e => setMainDescriptor(e.detail.value)}
                            placeholder="Short descriptive text for the demo"
                        />
                    </FormField>

                    <FormField label="Image URL (Optional)">
                        <Input
                            value={imageUrl}
                            onChange={e => setImageUrl(e.detail.value)}
                            placeholder="URL to demo preview image"
                        />
                    </FormField>

                    <FormField label="Video URL (Optional)">
                        <Input
                            value={videoUrl}
                            onChange={e => setVideoUrl(e.detail.value)}
                            placeholder="URL to demo preview video"
                        />
                    </FormField>

                    <FormField label="BuilderSpace URL (Optional)">
                        <Input
                            value={builderSpaceUrl}
                            onChange={e => setBuilderSpaceUrl(e.detail.value)}
                            placeholder="URL to related BuilderSpace project"
                        />
                    </FormField>

                    <FormField label="Embed">
                        <Select
                            selectedOption={{ label: embed ? 'Yes' : 'No', value: embed.toString() }}
                            onChange={e => setEmbed(e.detail.selectedOption.value === 'true')}
                            options={[
                                { label: 'Yes', value: 'true' },
                                { label: 'No', value: 'false' }
                            ]}
                        />
                    </FormField>

                    <SpaceBetween size="xs" direction="horizontal">
                        <Button onClick={() => navigate('/admin')}>Cancel</Button>
                        <Button variant="primary" onClick={handleSubmit}>{type === 'add' ? 'Create' : 'Update'} Demo</Button>
                    </SpaceBetween>
                </SpaceBetween>
            </Container>
        </SpaceBetween>
    );
}
